import React, { useState, useEffect, useCallback } from 'react';
import {
  FiShield, FiEdit2, FiTrash2, FiPlus, FiSave, FiX,
  FiUsers, FiCheck, FiAlertCircle, FiChevronDown, FiChevronUp,
} from 'react-icons/fi';
import { toast } from 'react-toastify';
import { perfilService } from '../../services/api';
import './PerfisUsuario.css';

// Módulos e labels
const MODULOS = [
  { key: 'dashboard',      label: 'Dashboard',         soLeitura: true  },
  { key: 'colaboradores',  label: 'Colaboradores',     soLeitura: false },
  { key: 'reunioes',       label: 'Reuniões',          soLeitura: false },
  { key: 'presencas',      label: 'Presenças',         soLeitura: false },
  { key: 'irmandade',      label: 'Irmandade',         soLeitura: false },
  { key: 'visitas',        label: 'Visitas',           soLeitura: false },
  { key: 'cargos',         label: 'Cargos',            soLeitura: false },
  { key: 'congregacoes',   label: 'Congregações',      soLeitura: false },
  { key: 'relatorios',     label: 'Relatórios',        soLeitura: true  },
  { key: 'recitativos',    label: 'Recitativos',       soLeitura: false },
  { key: 'reuniao_jovens',   label: 'Reunião de Jovens',   soLeitura: false },
  { key: 'reuniao_criancas', label: 'Reunião de Crianças', soLeitura: false },
  { key: 'rota_visitas',     label: 'Rota de Visitas',     soLeitura: false },
  { key: 'usuarios',         label: 'Usuários',            soLeitura: false },
];

const permissoesVazias = () =>
  Object.fromEntries(MODULOS.map((m) => [m.key, { read: false, write: false }]));

// ──── Modal de criação/edição de perfil ──────────────────────────────────────
const ModalPerfil = ({ perfil, onSalvar, onFechar }) => {
  const novo = !perfil;
  const [nome, setNome] = useState(perfil?.nome || '');
  const [slug, setSlug] = useState(perfil?.slug || '');
  const [descricao, setDescricao] = useState(perfil?.descricao || '');
  const [perms, setPerms] = useState(
    perfil?.permissoesModulos || permissoesVazias()
  );
  const [salvando, setSalvando] = useState(false);

  const setMod = (key, campo, val) =>
    setPerms((prev) => ({ ...prev, [key]: { ...prev[key], [campo]: val } }));

  const handleSalvar = async () => {
    if (!nome.trim()) { toast.error('Nome obrigatório'); return; }
    if (novo && !slug.trim()) { toast.error('Slug obrigatório'); return; }
    setSalvando(true);
    try {
      await onSalvar({
        nome: nome.trim(),
        slug: slug.trim(),
        descricao: descricao.trim() || null,
        permissoesModulos: perms,
      });
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="pu-overlay" onClick={(e) => e.target === e.currentTarget && onFechar()}>
      <div className="pu-modal">
        <div className="pu-modal-header">
          <h2>
            <FiShield />
            {novo ? 'Novo Perfil' : `Editar: ${perfil.nome}`}
          </h2>
          {perfil?.usuariosVinculados > 0 && (
            <span className="pu-cascade-aviso">
              <FiAlertCircle size={14} />
              {perfil.usuariosVinculados} usuário(s) serão atualizados ao salvar
            </span>
          )}
          <button className="pu-modal-fechar" onClick={onFechar}><FiX /></button>
        </div>

        <div className="pu-modal-body">
          <div className="pu-form-row">
            <label>
              <span>Nome do perfil</span>
              <input
                type="text"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex: Secretaria"
                disabled={perfil?.fixo}
              />
            </label>
            {novo && (
              <label>
                <span>Slug (identificador único)</span>
                <input
                  type="text"
                  value={slug}
                  onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                  placeholder="Ex: secretaria"
                />
              </label>
            )}
          </div>

          {!perfil?.fixo && (
            <label className="pu-form-full">
              <span>Descrição</span>
              <input
                type="text"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
                placeholder="Descreva brevemente o perfil"
              />
            </label>
          )}

          {/* Tabela de permissões */}
          <div className="pu-perm-section">
            <h3>Permissões por módulo</h3>
            <div className="pu-perm-table">
              <div className="pu-perm-thead">
                <span>Módulo</span>
                <span>Visualizar</span>
                <span>Editar</span>
              </div>
              {MODULOS.map((mod) => {
                const p = perms[mod.key] || { read: false, write: false };
                const rowOn = p.read || p.write;
                return (
                  <div key={mod.key} className={`pu-perm-row ${rowOn ? '' : 'row-off'}`}>
                    <span className="pu-perm-mod">{mod.label}</span>

                    <label className="pu-check">
                      <input
                        type="checkbox"
                        checked={p.read}
                        onChange={(e) => {
                          setMod(mod.key, 'read', e.target.checked);
                          if (!e.target.checked) setMod(mod.key, 'write', false);
                        }}
                      />
                      <span className={`pu-check-icon ${p.read ? 'on' : ''}`}>
                        {p.read && <FiCheck />}
                      </span>
                    </label>

                    {mod.soLeitura ? (
                      <span className="pu-na">—</span>
                    ) : (
                      <label className="pu-check">
                        <input
                          type="checkbox"
                          checked={p.write}
                          disabled={!p.read}
                          onChange={(e) => setMod(mod.key, 'write', e.target.checked)}
                        />
                        <span className={`pu-check-icon ${p.write ? 'on' : ''} ${!p.read ? 'disabled' : ''}`}>
                          {p.write && <FiCheck />}
                        </span>
                      </label>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="pu-modal-footer">
          <button className="pu-btn pu-btn-ghost" onClick={onFechar}>
            Cancelar
          </button>
          <button
            className="pu-btn pu-btn-primary"
            onClick={handleSalvar}
            disabled={salvando}
          >
            <FiSave /> {salvando ? 'Salvando…' : 'Salvar e aplicar'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ──── Card de perfil ─────────────────────────────────────────────────────────
const PerfilCard = ({ perfil, onEditar, onExcluir }) => {
  const [expandido, setExpandido] = useState(false);
  const perms = perfil.permissoesModulos || {};

  const modulosAtivos = MODULOS.filter(
    (m) => perms[m.key]?.read || perms[m.key]?.write
  );

  return (
    <div className={`pu-card ${perfil.fixo ? 'pu-card--fixo' : ''}`}>
      <div className="pu-card-header">
        <div className="pu-card-info">
          <div className="pu-card-nome">
            <FiShield className="pu-card-icon" />
            <strong>{perfil.nome}</strong>
            {perfil.fixo && <span className="pu-badge-fixo">padrão</span>}
          </div>
          {perfil.descricao && (
            <p className="pu-card-desc">{perfil.descricao}</p>
          )}
          <div className="pu-card-meta">
            <span className="pu-badge-users">
              <FiUsers size={12} /> {perfil.usuariosVinculados} usuário(s)
            </span>
            <span className="pu-badge-slug">#{perfil.slug}</span>
          </div>
        </div>

        <div className="pu-card-actions">
          <button
            className="pu-btn-icone"
            onClick={() => setExpandido(!expandido)}
            title={expandido ? 'Recolher' : 'Ver permissões'}
          >
            {expandido ? <FiChevronUp /> : <FiChevronDown />}
          </button>
          <button
            className="pu-btn-icone pu-btn-edit"
            onClick={() => onEditar(perfil)}
            title="Editar"
          >
            <FiEdit2 />
          </button>
          {!perfil.fixo && (
            <button
              className="pu-btn-icone pu-btn-danger"
              onClick={() => onExcluir(perfil)}
              title="Excluir"
            >
              <FiTrash2 />
            </button>
          )}
        </div>
      </div>

      {expandido && (
        <div className="pu-card-perms">
          <div className="pu-perm-table pu-perm-table--readonly">
            <div className="pu-perm-thead">
              <span>Módulo</span>
              <span>Visualizar</span>
              <span>Editar</span>
            </div>
            {MODULOS.map((mod) => {
              const p = perms[mod.key] || { read: false, write: false };
              return (
                <div key={mod.key} className={`pu-perm-row ${p.read || p.write ? '' : 'row-off'}`}>
                  <span className="pu-perm-mod">{mod.label}</span>
                  <span className={`pu-perm-dot ${p.read ? 'dot-on' : 'dot-off'}`}>
                    {p.read ? <FiCheck size={11} /> : '—'}
                  </span>
                  {mod.soLeitura ? (
                    <span className="pu-na">—</span>
                  ) : (
                    <span className={`pu-perm-dot ${p.write ? 'dot-on' : 'dot-off'}`}>
                      {p.write ? <FiCheck size={11} /> : '—'}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
          {modulosAtivos.length > 0 && (
            <p className="pu-perm-resumo">
              {modulosAtivos.length} módulo(s) com acesso
            </p>
          )}
        </div>
      )}
    </div>
  );
};

// ──── Página principal ───────────────────────────────────────────────────────
const PerfisUsuario = () => {
  const [perfis, setPerfis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // null | 'novo' | perfil-obj

  const carregar = useCallback(async () => {
    try {
      setLoading(true);
      const resp = await perfilService.listar();
      setPerfis(resp.data || []);
    } catch {
      toast.error('Erro ao carregar perfis');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const salvar = async (dados) => {
    try {
      if (modal === 'novo') {
        await perfilService.criar(dados);
        toast.success('Perfil criado!');
      } else {
        const resp = await perfilService.atualizar(modal.id, dados);
        const count = resp.data?.cascadeCount || 0;
        toast.success(
          count > 0
            ? `Perfil salvo! ${count} usuário(s) atualizados automaticamente.`
            : 'Perfil salvo!'
        );
      }
      setModal(null);
      carregar();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erro ao salvar perfil');
    }
  };

  const excluir = async (perfil) => {
    if (!window.confirm(`Excluir o perfil "${perfil.nome}"?`)) return;
    try {
      await perfilService.excluir(perfil.id);
      toast.success('Perfil excluído');
      carregar();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erro ao excluir');
    }
  };

  return (
    <div className="pu-page">
      <header className="pu-page-header">
        <div>
          <h1><FiShield className="pu-icon-title" /> Perfis de Usuário</h1>
          <p>Gerencie perfis e permissões. Alterações se propagam automaticamente para todos os usuários vinculados.</p>
        </div>
        <button
          className="pu-btn pu-btn-primary"
          onClick={() => setModal('novo')}
        >
          <FiPlus /> Novo perfil
        </button>
      </header>

      {loading ? (
        <div className="pu-empty">Carregando…</div>
      ) : (
        <div className="pu-lista">
          {perfis.map((p) => (
            <PerfilCard
              key={p.id}
              perfil={p}
              onEditar={(perf) => setModal(perf)}
              onExcluir={excluir}
            />
          ))}
        </div>
      )}

      {modal && (
        <ModalPerfil
          perfil={modal === 'novo' ? null : modal}
          onSalvar={salvar}
          onFechar={() => setModal(null)}
        />
      )}
    </div>
  );
};

export default PerfisUsuario;
